import type {
  AppVersion,
  Platform,
  PlatformFamily,
  PlatformPackage,
} from '@/types'

export const PLATFORM_FAMILY_LABEL: Record<PlatformFamily, string> = {
  windows: 'Windows',
  macos: 'macOS',
}

/** 列表 Tab：按家族筛，服务端 platform=macos 命中任一 darwin-* 新传 */
export const ALL_PLATFORM_FAMILIES: PlatformFamily[] = ['windows', 'macos']

/** 常见清单 key 的展示名；其它 key 按「家族 + 架构」兜底 */
const KNOWN_PLATFORM_LABEL: Record<string, string> = {
  'win32-x64': 'Windows',
  'darwin-arm64': 'macOS arm64',
  'darwin-x64': 'macOS Intel',
}

export function platformFamily(platform: string): PlatformFamily | undefined {
  if (platform.startsWith('win32-') || platform === 'windows') return 'windows'
  if (platform.startsWith('darwin-') || platform === 'macos') return 'macos'
  return undefined
}

/** 清单 key（win32-x64）与家族名（windows，来自 affected_platforms 等）都能展示 */
export function platformLabel(platform: string): string {
  const known = KNOWN_PLATFORM_LABEL[platform]
  if (known) return known
  const family = platformFamily(platform)
  if (!family) return platform
  const arch = platform.includes('-') ? platform.slice(platform.indexOf('-') + 1) : ''
  return arch
    ? `${PLATFORM_FAMILY_LABEL[family]} ${arch}`
    : PLATFORM_FAMILY_LABEL[family]
}

export interface PlatformChipStyle {
  background: string
  color: string
  border?: string
}

/** 接口偶发返回样式表之外的平台名时，避免整页崩掉 */
const UNKNOWN_PLATFORM_CHIP_STYLE: PlatformChipStyle = {
  background: 'rgba(0,0,0,0.04)',
  color: 'rgba(0,0,0,0.65)',
}

/** Figma 平台 chip 色：同家族同色，架构靠文案区分 */
const PLATFORM_FAMILY_CHIP_STYLE: Record<PlatformFamily, PlatformChipStyle> = {
  windows: { background: 'rgba(0, 136, 255, 0.08)', color: '#0088FF' },
  macos: { background: 'rgba(255, 141, 40, 0.08)', color: '#FF8D28' },
}

export function platformChipStyle(platform: string): PlatformChipStyle {
  const family = platformFamily(platform)
  return family ? PLATFORM_FAMILY_CHIP_STYLE[family] : UNKNOWN_PLATFORM_CHIP_STYLE
}

/** 槽位有包：upload_id / file_id / package_url 任一非空（老应用可能只有 file_id） */
export function hasPackageFile(pkg: PlatformPackage): boolean {
  return (
    !!pkg.upload_id?.trim() ||
    !!pkg.file_id?.trim() ||
    !!pkg.package_url?.trim()
  )
}

/** 槽位有包且已挂上：直传未 complete（pending）/ 失败的不算 */
export function hasReadyPackageFile(pkg: PlatformPackage): boolean {
  return (
    pkg.parse_status !== 'pending' &&
    pkg.parse_status !== 'failed' &&
    hasPackageFile(pkg)
  )
}

/**
 * 本版新传子包：非沿用且槽位有包。详情会给声明平台返回空占位项，不算；
 * 直传 init 后未 complete 的 pending 项也不算。
 * 本次会话内上传接口已成功的也算（接口未回包字段时兜底）。
 */
export function isPlatformPackageUploaded(
  pkg: PlatformPackage | undefined,
): pkg is PlatformPackage {
  return (
    !!pkg &&
    !pkg.is_historical &&
    pkg.parse_status !== 'pending' &&
    pkg.parse_status !== 'failed' &&
    (hasPackageFile(pkg) || !!pkg.uploaded_locally)
  )
}

/**
 * 后端确认的沿用历史包：parse-result historical_packages 或详情 is_historical，
 * 均带 from_version_id；前端不再自行推算历史包。
 */
export function isBackendHistoricalPackage(pkg: PlatformPackage): boolean {
  return pkg.is_historical && !!pkg.from_version_id?.trim()
}

/** 可用于发布：本版本已上传，或沿用后端确认的历史包 */
export function isPlatformPackageUsable(pkg: PlatformPackage): boolean {
  return isBackendHistoricalPackage(pkg) || isPlatformPackageUploaded(pkg)
}

/** 比较 semver 字符串，大的在前（倒序用） */
export function compareVersionDesc(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0)
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const d = (pb[i] ?? 0) - (pa[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

const PUBLISHED_STATUSES = new Set(['released', 'delisted', 'disabled'])

/** 曾上线过：上线后被下架 / 停用 / 顶替都算；取消定时等从未上线的 archived 不算 */
export function hasEverReleased(
  v: Pick<AppVersion, 'status' | 'released_at'>,
): boolean {
  return (
    PUBLISHED_STATUSES.has(v.status) ||
    (v.status === 'archived' && !!v.released_at)
  )
}

/** 版本号下限兜底（服务端 max_online_version 缺失时）：曾上线过的最高版本 */
export function maxEverReleasedVersion(
  versions: Pick<AppVersion, 'status' | 'version' | 'released_at'>[],
): string | undefined {
  return versions
    .filter(hasEverReleased)
    .map((v) => v.version)
    .sort(compareVersionDesc)[0]
}

/** 从曾上线的版本中取某平台最近可用包，供「未勾选则沿用历史包」 */
export function findHistoricalPlatformPackage(
  versions: AppVersion[],
  platform: Platform,
  excludeVersionId?: string,
): PlatformPackage | undefined {
  const candidates = versions
    .filter(
      (v) =>
        v.id !== excludeVersionId &&
        hasEverReleased(v) &&
        v.platforms.includes(platform),
    )
    .sort((a, b) => compareVersionDesc(a.version, b.version))

  for (const v of candidates) {
    const existing = v.platform_packages?.find((p) => p.platform === platform)
    if (existing) {
      return {
        platform,
        file_name: existing.file_name,
        size_mb: existing.size_mb,
        package_version: existing.package_version || v.version,
        is_historical: true,
      }
    }
    return {
      platform,
      file_name: v.main_package?.file_name,
      size_mb: v.main_package?.size_mb,
      package_version: v.version,
      is_historical: true,
    }
  }
  return undefined
}

/** 批量为声明平台解析可沿用的历史包 */
export function resolveHistoricalPlatformPackages(
  versions: AppVersion[],
  platforms: Platform[],
  excludeVersionId?: string,
): PlatformPackage[] {
  return platforms
    .map((p) => findHistoricalPlatformPackage(versions, p, excludeVersionId))
    .filter((x): x is PlatformPackage => !!x)
}
